<script setup lang="ts">
import { ref } from 'vue'
import {
  IconBriefcase,
  IconCalendarEvent,
  IconCamera,
  IconCheck,
  IconHeart,
  IconMail,
  IconPray,
  IconSchool,
  IconSparkles,
  IconUser,
  IconWorld,
  IconBrandFacebook,
  IconCloudDataConnection,
} from '@tabler/icons-vue'
import EducationWizard from '@/components/wizards/EducationWizard.vue'

type WizardTileId = 'personal' | 'education' | 'career' | 'travel' | 'relationships' | 'religion'
type WizardTile = {
  id: WizardTileId
  label: string
  icon: any
  selected: boolean
}

const wizardTiles: WizardTile[] = [
  { id: 'personal', label: 'Personal', icon: IconUser, selected: true },
  { id: 'education', label: 'Education', icon: IconSchool, selected: false },
  { id: 'career', label: 'Career', icon: IconBriefcase, selected: false },
  { id: 'travel', label: 'Travel', icon: IconWorld, selected: true },
  { id: 'relationships', label: 'Relationships', icon: IconHeart, selected: true },
  { id: 'religion', label: 'Religion', icon: IconPray, selected: false },
]

const integrations = [
  {
    id: 'camera-roll',
    name: 'Camera Roll',
    description: 'Intelligently pick meaningful photos and auto-import them for your review.',
    icon: IconCamera,
    status: 'Connected',
    connected: true,
  },
  {
    id: 'calendar',
    name: 'Calendar',
    description: 'Pull milestones and events into timelines automatically.',
    icon: IconCalendarEvent,
    status: 'Connected',
    connected: true,
  },
  {
    id: 'email',
    name: 'Email',
    description: 'Scan receipts, travel, and family updates for timeline drafts.',
    icon: IconMail,
    status: 'Suggested',
    connected: false,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    description: 'Import posts and photo moments as timeline candidates.',
    icon: IconBrandFacebook,
    status: 'Suggested',
    connected: false,
  },
]

const activeSection = ref<'wizards' | 'integrations'>('wizards')
const showEducationWizard = ref(false)
const wizardSuccess = ref<string | null>(null)

function handleWizardTileClick(tile: WizardTile) {
  if (tile.id === 'education') {
    wizardSuccess.value = null
    showEducationWizard.value = true
  }
}

function handleEducationWizardCreated(message: string) {
  wizardSuccess.value = message
}
</script>

<template>
  <div class="ideas-view w-full max-w-6xl mx-auto px-4 py-5 md:px-6 md:py-6 text-base-content">
    <section class="flex flex-col gap-2">
      <div class="flex items-center gap-4">
        <div>
          <div class="rounded-xl bg-primary/10 p-2 text-primary">
            <IconSparkles class="h-8 w-8" />
          </div>
        </div>
        <div>
          <h1 class="text-2xl md:text-3xl font-semibold">Time Assist</h1>
        </div>
      </div>
      <p class="text-sm text-base-content/70">
        You've got a personal biographer in your pocket! Use the wizards and integrations below to
        fast track your timeline.
      </p>
    </section>

    <div class="h-2 my-2 rounded-full bg-primary" />
    <div
      v-if="wizardSuccess"
      class="mt-3 rounded-lg border border-success/30 bg-success/10 p-3 text-sm text-success"
    >
      {{ wizardSuccess }}
    </div>

    <section class="mt-6">
      <div class="join w-full">
        <button
          type="button"
          class="join-item btn flex-1"
          :class="activeSection === 'wizards' ? 'btn-primary' : 'btn-outline'"
          @click="activeSection = 'wizards'"
        >
          Wizards
        </button>
        <button
          type="button"
          class="join-item btn flex-1"
          :class="activeSection === 'integrations' ? 'btn-primary' : 'btn-outline'"
          @click="activeSection = 'integrations'"
        >
          Integrations
        </button>
      </div>
    </section>

    <section v-if="activeSection === 'wizards'" class="mt-6">
      <div class="mb-3 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold">Wizards</h2>
          <p class="text-sm text-base-content/70">
            Structured prompts for you and your child's timeline.
          </p>
        </div>
        <span class="badge badge-primary badge-outline">Guided</span>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
        <button
          v-for="tile in wizardTiles"
          :key="tile.id"
          type="button"
          class="card card-compact border border-base-300 bg-base-100 text-left transition-colors hover:border-primary/50 hover:bg-base-200"
          @click="handleWizardTileClick(tile)"
        >
          <div class="card-body relative min-h-30 items-center justify-center text-center p-5">
            <component :is="tile.icon" class="h-8 w-8 text-primary" />
            <span class="font-medium">{{ tile.label }}</span>
            <IconCheck v-if="tile.selected" class="absolute right-3 top-3 h-5 w-5 text-primary" />
            <span
              v-else
              class="absolute right-3 top-3 inline-block h-4 w-4 rounded-full border border-base-content/40"
            />
          </div>
        </button>
      </div>
    </section>

    <section v-else class="mt-6">
      <div class="mb-3 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold">Integrations</h2>
          <p class="text-sm text-base-content/70">
            Connect services to continually draft timeline moments.
          </p>
        </div>
        <span class="badge badge-primary badge-outline ml-1">Continuous</span>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <article
          v-for="integration in integrations"
          :key="integration.id"
          class="card border border-base-300 bg-base-100 shadow-sm"
        >
          <div class="card-body gap-4">
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-center gap-3">
                <div class="rounded-xl integrations-icon-wrap p-2">
                  <component :is="integration.icon" class="h-6 w-6 integrations-icon" />
                </div>
                <div>
                  <h3 class="card-title text-base">{{ integration.name }}</h3>
                  <p class="text-xs text-base-content/70">{{ integration.status }}</p>
                </div>
              </div>
              <IconCloudDataConnection
                class="h-5 w-5"
                :class="integration.connected ? 'text-primary' : 'text-base-content/50'"
              />
            </div>

            <p class="text-sm text-base-content/80">{{ integration.description }}</p>

            <div class="card-actions justify-end">
              <button v-if="integration.connected" type="button" class="btn btn-sm btn-primary">
                Manage
              </button>
              <button v-else type="button" class="btn btn-sm btn-outline integrations-btn">
                Connect
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <EducationWizard
      :open="showEducationWizard"
      @update:open="showEducationWizard = $event"
      @created="handleEducationWizardCreated"
    />
  </div>
</template>

<style scoped>
.ideas-view {
  --ideas-iris-surface: var(--color-iris-100);
  --ideas-iris-border: var(--color-iris-300);
  --ideas-iris-border-hover: var(--color-iris-500);
  --ideas-iris-text: var(--color-iris-700);
  --ideas-iris-text-strong: var(--color-iris-800);
}

.integrations-badge {
  background-color: var(--ideas-iris-surface);
  color: var(--ideas-iris-text-strong);
}

.integrations-icon-wrap {
  background-color: var(--ideas-iris-surface);
}

.integrations-icon {
  color: var(--ideas-iris-text);
}

.integrations-btn {
  border-color: var(--ideas-iris-border);
  color: var(--ideas-iris-text);
}

.integrations-btn:hover {
  border-color: var(--ideas-iris-border-hover);
  background-color: var(--ideas-iris-surface);
}
</style>
